
import { NgClass } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { MenuButtonComponent } from "@components/menu-button/menu-button.component";
import { NgbModal, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import _ from "lodash";
import { Subject, forkJoin } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MassupdateArbetsordrarComponent } from "~app/common/components/massupdate-arbetsordrar/massupdate-arbetsordrar.component";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { DomService } from "~app/common/services/dom.service";
import { ArbestorderDestineringsalternativViewModel } from "~app/features/planera/arbetsorder-destineringsalternativ/arbetsorder-destineringsalternativ.models";
import {
    ArbetsorderDestineringsalternativManuellaComponent
} from "~app/features/planera/arbetsorder-destineringsalternativ/manuella/arbetsorder-destineringsalternativ-manuella.component";
import { ArbetsorderService } from "~app/features/planera/services/arbetsorder.service";
import { ArbetsorderListHoverInMessage, ArbetsorderListHoverOutMessage, ArbetsorderMapHoverInMessage, ArbetsorderMapHoverOutMessage } from "../../events/ArbetsorderHoverMessage";
import { ITplArbetsorder } from "../../models/dtos";
import { TplArbetsorderViewModel } from "../../models/tplListViewModels";
import { TplStateService } from "../../state/tpl-state.service";
import { TplArbetsorderInfoModalComponent } from "../tpl-arbetsorder-info-modal/tpl-arbetsorder-info-modal.component";
import {
    ITplArbetsordrarDestineringsalternativManuellaResult,
    TplArbetsordrarDestineringsalternativManuellaComponent
} from "../tpl-arbetsordrar-destineringsalternativ-manuella/tpl-arbetsordrar-destineringsalternativ-manuella.component";

@Component({
    selector: "rask-tpl-hantera-ao",
    templateUrl: "./tpl-hantera-ao.component.html",
    styleUrls: ["./tpl-hantera-ao.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        MenuButtonComponent,
        ActionButtonComponent,
        CheckboxComponent,
        FormsModule,
        NgClass,
        NgbTooltip,
        TranslatePipe
    ]
})
export class TplHanteraAoComponent implements OnInit, OnDestroy {

    arbetsorders: TplArbetsorderViewModel[];
    nrOfSelectedAos = 0;
    allSelected = false;
    canMasshantera = false;
    nrOfProduktionsomraden = 0;
    nrOfHandelssortiments = 0;
    @Input() canEdit: boolean;

    cmp: ComponentRef<any>;
    instance: MassupdateArbetsordrarComponent = null;

    readonly tabName = "hanteraao";
    private readonly ngUnsubscribe$ = new Subject<void>();

    private readonly eventService = inject(EventService);
    private readonly modalService = inject(NgbModal);
    private readonly tplStateService = inject(TplStateService);
    private readonly domService = inject(DomService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    protected get updateEnabled(): boolean {
        return this.canEdit && this.nrOfSelectedAos > 0 && this.nrOfSelectedAos <= 1000;
    }

    protected get canEditManuellaDestineringsalternativ(): boolean {
        return this.updateEnabled && this.nrOfProduktionsomraden === 1 && this.nrOfHandelssortiments === 1;
    }

    ngOnInit() {
        const state = this.tplStateService.current;

        state.currentArbetsordrar
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(current => {
                this.createViewModel(current);
            });

        state.selected
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                if (this.arbetsorders && event.origin !== "hanteraao") {
                    this.arbetsorders.forEach(ao => {
                        ao.isSelected = event.selected.some(id => id === ao.id);
                    });
                    this.updateSelectedView();
                }
            });

        this.eventService.subscribeEvent<ArbetsorderMapHoverInMessage>(ArbetsorderMapHoverInMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event: ArbetsorderMapHoverInMessage) => {
                this.arbetsorders.find(p => p.id === event.arbetsorderId).isHover = true;
                this.triggerDigestCycle();
            });

        this.eventService.subscribeEvent<ArbetsorderMapHoverOutMessage>(ArbetsorderMapHoverOutMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event: ArbetsorderMapHoverOutMessage) => {
                this.arbetsorders.find(p => p.id === event.arbetsorderId).isHover = false;
                this.triggerDigestCycle();
            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    protected aoDetails($event, arbetsorderId: number) {
        $event.preventDefault();

        const arbetsorder = this.arbetsorders.find(ao => ao.id === arbetsorderId);
        TplArbetsorderInfoModalComponent.open({
            arbetsorderId: arbetsorderId,
            isSelected: arbetsorder.isSelected,
            showSelected: true
        }, this.modalService, (result) => {
            if (arbetsorder.selected !== result.isSelected) {
                arbetsorder.selected = result.isSelected;
                this.onCheckedChanged(arbetsorder.selected);
            }

            if (result.hasAddedDestineringsalternativ === true) {
                arbetsorder.clearWarnings();
            }
        });
    }

    protected updateDestineringsalternativ() {
        this.addComponentToBody();
        this.instance.header = "Uppdaterar destineringsalternativ";
        this.instance.arbetsordrar = this.arbetsorders.filter(x => x.isSelected);
        const checkAddIntressent = true;

        setTimeout(() => {
            this.instance.uppdateraDestineringsalternativ(checkAddIntressent);
        },1000);
    }

    protected uppdateraVirkesordrar() {
        this.addComponentToBody();
        this.instance.header = "Uppdaterar från regelverk";
        this.instance.arbetsordrar = this.arbetsorders.filter(x => x.isSelected);
        const checkAddIntressent = true;

        setTimeout(() => {
            this.instance.uppdateraVirkesordrar(checkAddIntressent);
        },1000);
    }

    protected addDestineringsalternativ() {
        // En bugg att man kan klicka på en icon fast knappen/länken är disabled
        if (!this.canEditManuellaDestineringsalternativ) {
            return;
        }

        const selectedArbetsorderIden = this.arbetsorders.filter(x => x.isSelected).map(x => x.id);
        if (selectedArbetsorderIden.length === 1) {
            this.addSingelArbetsorderDestineringsalternativ();
        }else{
            this.addMultiArbetstordersDestineringsalternativ();
        }
    }

    protected onAllSelectedChanged($event: boolean) {
        this.allSelected = $event;
        this.arbetsorders.forEach(x => {
            x.isSelected = $event;
        });
        this.updateSelectedView();
        this.signalSelected();
    }

    protected onCheckedChanged($event: any) {
        setTimeout(() => {
            this.updateSelectedView();
            if ($event != null) {
                this.signalSelected();
            }
        }, 200);
    }

    protected updateSelectedView() {
        this.setNrOfSelectedAos();
        this.sortArbetsorders();
    }

    protected mouseOver(arbetsorderId: number) {
        this.arbetsorders.find(p => p.id === arbetsorderId).isHover = true;
        this.eventService.emit(new ArbetsorderListHoverInMessage(arbetsorderId));
    }

    protected mouseOut(arbetsorderId: number) {
        this.arbetsorders.find(p => p.id === arbetsorderId).isHover = false;
        this.eventService.emit(new ArbetsorderListHoverOutMessage(arbetsorderId));
    }

    private addMultiArbetstordersDestineringsalternativ() {
        const selectedArbetsorderIden = this.arbetsorders.filter(x => x.isSelected).map(x => x.id);
        const selectedHandelssortiment = this.tplStateService.current.getSelectedHandelssortiment()[0];
        const data = {
            arbetsorderIdList: selectedArbetsorderIden,
            handelssortimentId: selectedHandelssortiment
        };
        this.raskModalService.openAsync(
            "Arbetsordrars Manuella destineringsalternativ",
            TplArbetsordrarDestineringsalternativManuellaComponent,
            "lg",
            true,
            data)
            .subscribe((response: ITplArbetsordrarDestineringsalternativManuellaResult) => {
                if (!response) {
                    return;
                }

                response.successArbetsordersIdList.forEach(aoId => {
                    const arbetsorder = this.arbetsorders.find(x => x.id === aoId);
                    arbetsorder.isSelected = false;
                    arbetsorder.clearWarnings();
                });

                this.arbetsorders.filter(x => x.isSelected).forEach(ao => {
                    ao.clearWarnings();
                    ao.warnings.push({
                        brief: "Destineringsalternativ kunde inte uppdateras",
                        long: "Destineringsalternativ kunde inte uppdateras",
                        type: "warning"
                    });
                });

                this.signalSelected();
            });
    }

    private signalSelected() {
        const currentSelected = this.arbetsorders.filter(x => x.isSelected).map(x => x.model.id);
        this.tplStateService.current.setSelected({ selected: currentSelected,
            origin: "hanteraao" });
    }

    private addSingelArbetsorderDestineringsalternativ() {
        const selectedArbetsordrar = this.arbetsorders.filter(x => x.isSelected);
        if (selectedArbetsordrar.length !== 1) {
            return;
        }
        const arbetsorder = selectedArbetsordrar[0];
        const ahs$ = this.arbetsorderService.getArbetsorderHandelssortiment(arbetsorder.id);
        const destineringar$ = this.arbetsorderService.getDestineringsalternativ(arbetsorder.id);

        forkJoin([
            ahs$,
            destineringar$
        ])
            .subscribe(([
                ah,
                dest
            ]) => {
                const arbetsorderHandelssortimentsLista = ah.arbetsorderHandelssortiment;
                const destineringsalternativLista = dest.destineringsalternativ;
                const viewModel = new ArbestorderDestineringsalternativViewModel(arbetsorderHandelssortimentsLista, destineringsalternativLista);
                const valdaManuella = [];

                viewModel.arbetsorderHandelssortiment.forEach(hs => {
                    hs.destineringsalternativ
                        .filter(x => x.isManuellRad)
                        .forEach(da => {
                            valdaManuella.push({
                                foradlatHandelssortimentId: da.foradlatHandelssortimentId,
                                handelssortimentId: da.arbetsorderHandelssortiment.handelssortimentId,
                                mottagningsplatsId: da.mottagningsplatsId
                            });
                        });
                });

                const data = {
                    arbetsorder: arbetsorder,
                    valdaManuellaRader: valdaManuella,
                    checkAddArbetsorderIntressent: true
                };

                this.raskModalService.openAsync(
                    "Manuella destineringsalternativ",
                    ArbetsorderDestineringsalternativManuellaComponent,
                    "lg",
                    true,
                    data)
                    .subscribe(result => {
                        if (result === true) {
                            arbetsorder.clearWarnings();
                            arbetsorder.isSelected = false;
                        }
                    });
            });
    }

    private addComponentToBody() {
        this.cmp = this.domService.appendComponentToBody(MassupdateArbetsordrarComponent);
        this.instance = this.cmp.instance;
        this.instance.cmp = this.cmp;
    }

    private sortArbetsorders() {
        this.arbetsorders = _.orderBy(this.arbetsorders, [
            "isSelected",
            "northing"
        ], [
            "desc",
            "desc"
        ]);
    }

    private setNrOfSelectedAos() {
        this.nrOfSelectedAos = this.arbetsorders?.filter(x => x.isSelected).length ?? 0;
        this.canMasshantera = this.nrOfSelectedAos > 0;
        this.allSelected = this.nrOfSelectedAos > 0 && this.nrOfSelectedAos === (this.arbetsorders?.length ?? 0);
        this.nrOfProduktionsomraden = this.arbetsorders.map(item => item.produktionsomradeId)
            .filter((value, index, self) => self.indexOf(value) === index).length;
        this.nrOfHandelssortiments = this.tplStateService.current.getSelectedHandelssortiment()?.length ?? 0;
    }

    private createViewModel(arbetsordrar: ITplArbetsorder[]) {
        this.arbetsorders = arbetsordrar.map(x => new TplArbetsorderViewModel(x, null, null, null));
        this.sortArbetsorders();
    }

    private triggerDigestCycle(): void {
        this.changeDetectorRef.detectChanges();
    }
}
